import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';

/**
 * Contexte d'action d'une tâche (ADR-026 §4) : guidance déterministe, étape du guide généré et sa
 * validité, commandes et leur décision de politique — affichées, jamais accordées ici : chaque
 * commande est ré-évaluée par le serveur à l'exécution. Un guide périmé est signalé comme tel.
 */
interface ActionContextData {
  step: { step_id: string; label?: string };
  commands: Array<{ command: string; permitted: boolean; reason?: string }>;
  guides?: Array<{ knowledge_id: string; title: string; validity_status: string; step?: { step_id: string; title?: string; instruction: string } }>;
  guidance: Array<{ kind: string; text: string }>;
}

const KIND_LABEL: Readonly<Record<string, string>> = { instruction: 'Consigne', required_action: 'Action attendue', warning: 'Attention', validation: 'Contrôle' };

export function ActionContextPanel({ workItemId }: { workItemId: string }) {
  const services = useAppServices();
  const [loaded] = useLoad<ActionContextData>(`action-context:${workItemId}`, async () => (await services.client.query<ActionContextData>('GetActionContext', { work_item_id: workItemId })).data);
  if (loaded.status !== 'ready') return null;
  const context = loaded.value;
  // Seul ce qui parle à l'opérateur s'affiche (maquette du 2026-10-20) : la consigne est déjà sous le titre de la
  // tâche, l'action attendue et les commandes refusées sont des noms techniques ; sans rien d'autre, pas de panneau.
  const guidance = context.guidance.filter((entry) => entry.kind === 'warning' || entry.kind === 'validation');
  const guides = context.guides ?? [];
  if (guidance.length === 0 && guides.length === 0) return null;
  return (
    <section className="ay-card" aria-labelledby="ay-action-context-title">
      <h2 id="ay-action-context-title">Guide de l’étape</h2>
      {guidance.length > 0 && (
        <ul className="ay-list">
          {guidance.map((entry, index) => (
            <li key={`${entry.kind}-${index}`}>
              <strong>{KIND_LABEL[entry.kind] ?? entry.kind} :</strong> {entry.text}
            </li>
          ))}
        </ul>
      )}
      {guides.map((guide) => (
        <p key={guide.knowledge_id} className="ay-text ay-text--muted">
          {guide.title}
          {guide.validity_status === 'current' ? '' : ' — guide périmé : le workflow a changé depuis sa génération.'}
        </p>
      ))}
    </section>
  );
}
